import { enableProdMode, provideZoneChangeDetection } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import * as Sentry from '@sentry/angular';
import { Capacitor } from '@capacitor/core';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';

function trackStartupPhase(phase: string, data?: Record<string, unknown>): void {
  Sentry.addBreadcrumb({
    category: 'startup',
    message: phase,
    level: 'info',
    data,
  });
}

if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    Sentry.captureException(event.error || new Error(event.message), {
      tags: { phase: 'window.error' },
      extra: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    Sentry.captureException(event.reason || new Error('Unhandled promise rejection'), {
      tags: { phase: 'window.unhandledrejection' },
    });
  });
}

// Initialize Sentry for web/browser
Sentry.init({
  dsn: environment.sentry.dsn,
  environment: environment.sentry.environment,
  integrations: [
    Sentry.browserTracingIntegration({
      // Don't trace interactions on these elements to reduce overhead
      shouldCreateSpanForRequest: (url) => {
        // Skip tracing for assets and frequent requests
        return !url.includes('/assets/') &&
               !url.includes('maps.googleapis.com') &&
               !url.includes('analytics');
      },
    }),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: environment.production ? 0.1 : 1.0,

  // Performance optimization: Filter out noise
  beforeSend(event, hint) {
    // Don't send errors for common browser issues
    if (event.exception) {
      const error = hint.originalException;
      if (error && typeof error === 'string') {
        // Skip network errors and common browser issues
        if (error.includes('Network Error') ||
            error.includes('ERR_NETWORK') ||
            error.includes('Script error')) {
          return null;
        }
      }
    }

    // Only send a sample of non-critical events in production
    if (environment.production && event.level === 'info') {
      return Math.random() < 0.1 ? event : null; // 10% sampling for info events
    }

    return event;
  },

  // Session Replay - Optimized for performance
  replaysSessionSampleRate: 0.05, // Reduced from 10% to 5%
  replaysOnErrorSampleRate: 0.5, // Reduced from 100% to 50% for errors
});

trackStartupPhase('sentry_initialized', {
  production: environment.production,
});

if (environment.production) {
  enableProdMode();
  trackStartupPhase('production_mode_enabled');
}

if (Capacitor.isNativePlatform() && typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  trackStartupPhase('native_service_worker_cleanup_started');
  navigator.serviceWorker.getRegistrations()
    .then((registrations) => Promise.all(registrations.map((registration) => registration.unregister())))
    .then(() => {
      trackStartupPhase('native_service_worker_cleanup_finished');
    })
    .catch(() => {
      // Ignore cleanup errors and continue app bootstrap.
      trackStartupPhase('native_service_worker_cleanup_failed');
    });
}

trackStartupPhase('angular_bootstrap_started');

platformBrowserDynamic().bootstrapModule(AppModule, { applicationProviders: [provideZoneChangeDetection()], })
  .then(() => {
    trackStartupPhase('angular_bootstrap_finished');
  })
  .catch(err => {
    Sentry.captureException(err, {
      tags: { phase: 'angular_bootstrap_failed' },
    });
    console.log(err);
  });
